import {localize} from '../utils/Localization';
import {bindingLabel} from '../input/bindings.js';
import {mobileLevelControls, levelKeyId} from '../input/mobileLevelControls.js';
import '../css/MobileLevelMenu.scss';

export const mobileKeyLabel = code => bindingLabel({code}, true, localize);

export class MobileLevelMenu {
    constructor({toggle, pin, wire, release}) {
        this.pin = pin; this.wire = wire; this.release = release;
        this.seen = new Set();
        this.root = document.createElement('div');
        this.root.className = 'mobile-level-menu';
        this.root.hidden = true;
        this.root.innerHTML = `<p class="mobile-level-hint" role="status" hidden></p>
            <section class="mobile-level-drawer" hidden>
                <div class="mobile-level-heading"><strong></strong><button type="button" class="mobile-level-close">×</button></div>
                <p class="mobile-level-help"></p><div class="mobile-level-list"></div>
            </section>`;
        document.body.appendChild(this.root);
        this.toggle = toggle;
        this.toggle.setAttribute('aria-expanded', 'false');
        this.drawer = this.root.querySelector('.mobile-level-drawer');
        this.hint = this.root.querySelector('.mobile-level-hint');
        this.toggle.onclick = () => this.setOpen(this.drawer.hidden);
        const close = this.root.querySelector('.mobile-level-close');
        close.setAttribute('aria-label', localize('controls_close'));
        close.onclick = () => this.setOpen(false);
        // Keep focus on the game while tapping menu commands or holding keys.
        for (const element of [this.root, this.toggle]) {
            element.addEventListener('pointerdown', event => { event.preventDefault(); event.stopPropagation(); });
            for (const type of ['touchstart', 'touchend', 'touchcancel']) element.addEventListener(type, event => event.stopPropagation());
        }
    }
    render(orientation) {
        this.setOpen(false);
        this.disposers = [];
        const {keys, layouts} = mobileLevelControls;
        this.orientation = orientation;
        this.toggle.textContent = `${localize('controls_level_title')} (${keys.length})`;
        this.drawer.querySelector('strong').textContent = localize('controls_level_title');
        this.drawer.querySelector('.mobile-level-help').textContent = localize('controls_level_help');
        const list = this.root.querySelector('.mobile-level-list');
        list.replaceChildren();
        for (const code of keys) {
            const row = document.createElement('div');
            row.className = 'mobile-level-row';
            const key = document.createElement('button');
            key.type = 'button'; key.className = 'mobile-level-press'; key.dataset.levelKey = code;
            key.textContent = mobileKeyLabel(code);
            this.disposers.push(this.wire(key, code));
            const pin = document.createElement('button');
            pin.type = 'button'; pin.dataset.pinKey = code;
            const pinned = !!layouts[orientation][levelKeyId(code)];
            pin.textContent = localize(pinned ? 'controls_level_remove' : 'controls_level_add');
            pin.setAttribute('aria-label', `${pin.textContent}: ${mobileKeyLabel(code)}`);
            pin.onclick = () => { this.pin(code, !pinned); this.setOpen(true); };
            row.append(key, pin); list.appendChild(row);
        }
    }
    resetTouches() { this.disposers?.forEach(reset => reset()); }
    position(viewport, launcher) {
        const {left, top, height} = launcher;
        const drawerWidth = Math.min(320, viewport.width - 16);
        const drawerLeft = Math.max(8, Math.min(left, viewport.width - drawerWidth - 8));
        const below = top + height + 8;
        const drawerTop = below < viewport.height / 2 ? below : 8;
        Object.assign(this.drawer.style, {left: `${drawerLeft}px`, top: `${drawerTop}px`, width: `${drawerWidth}px`,
            maxHeight: `${Math.max(80, viewport.height - drawerTop - 8)}px`});
        Object.assign(this.hint.style, {left: `${drawerLeft}px`, top: `${Math.min(below, viewport.height - 100)}px`, width: `${drawerWidth}px`});
    }
    setOpen(open) {
        this.release(); this.drawer.hidden = !open;
        this.toggle.setAttribute('aria-expanded', String(open));
        if (open) this.dismissHint();
    }
    dismissHint() { this.hint.hidden = true; clearTimeout(this.hintTimer); }
    show(visible) {
        this.root.hidden = !visible || !mobileLevelControls.keys.length;
        this.toggle.hidden = this.root.hidden;
        if (this.root.hidden) { this.setOpen(false); this.dismissHint(); return; }
        const id = mobileLevelControls.id;
        if (!this.seen.has(id)) {
            this.seen.add(id);
            this.hint.textContent = localize('controls_level_hint');
            this.hint.hidden = false;
            this.hintTimer = setTimeout(() => this.dismissHint(), 7000);
        }
    }
}
